import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18n';
import {
  ChannelGroupManager,
  DefaultChannelGroupManager
} from 'core/channelGroup/ChannelGroupManager';
import {
  ChannelGroupDTO,
  ChannelGroupFormData,
  ChannelGroupListDTO
} from 'core/channelGroup/ChannelGroup';
import { useCallAPI } from 'hooks/useCallAPI';
import { ROUTE_PATH } from 'enum/RoutePath';
import { BreadcrumbsRoute } from 'use-react-router-breadcrumbs';
import { DynamicBreadcrumb } from 'components/Breadcrumbs/DynamicBreadcrumbs';
import { useParams } from 'react-router-dom';
import { AgencyManager, Currency, DefaultAgencyManager } from 'core';
import {
  AdRequestSourceManager,
  DefaultAdRequestSourceManager
} from 'core/adRequestSource/AdRequestSourceManager';
import { createSelectOptions } from 'utils/SelectOptionsUtils';
import { toBrandFormValues } from 'core/channelGroup/channelGroupBrand';

export enum ChannelGroupTabs {
  BASIC = 'basic',
  SPECIAL_SETTINGS = 'specialSettings',
  REPORT = 'report',
  BID_PRICE = 'bidPrice',
  AD_TYPES = 'adTypes',
  OUTDOOR_CREATIVE_SIZES = 'outdoorCreativeSizes',
  NON_EXPRESS_PASS_CATEGORIES = 'nonExpressPassCategories',
  // ECOPD-5581: 只在電子紙渠道出現
  EPAPER_DEFAULT_IMAGE = 'epaperDefaultImage',
  // ECOPD-5701: 只在詳情頁出現（編輯表單沒有這個 tab）
  AUDIT_LOG = 'auditLog'
}

export type ChannelGroupFormModelData = {
  loading: boolean;
  title: string;
  isEditing: boolean;
  tab: ChannelGroupTabs;
  redirectPathData?: {
    pathname: string;
  };
  agencyOptions: SelectOptions[];
  currencyOptions: SelectOptions[];
  tenmaxCategoryOptions: SelectOptions[];
  breadcrumbsRoutes?: BreadcrumbsRoute[];
  channelGroupId: number;
  initChannelGroup?: ChannelGroupFormData;
  onTabChange: (tab: string | null) => void;
  submit: (formData: ChannelGroupFormData) => void;
  cancel: () => void;
};

/**
 * 品牌欄位的表單初值：文字欄位補成字串、兩張圖示展開成 FileInput 要的 { url }。
 * 送出時由 wrapForServer 換回 property.addonProps.brand，沒動過的欄位原值送回。
 */
export const toBrandFormData = (
  channelGroup: ChannelGroupDTO
): Pick<
  ChannelGroupFormData,
  'property' | 'brandFilteredIconData' | 'brandNormalIconData'
> => {
  const brand = channelGroup.property.addonProps.brand;
  const toImageData = (url: string | null | undefined): ImageFieldData =>
    url ? { url } : {};
  return {
    property: {
      ...channelGroup.property,
      addonProps: {
        ...channelGroup.property.addonProps,
        brand: toBrandFormValues(brand)
      }
    },
    brandFilteredIconData: toImageData(brand?.filteredIconUrl),
    brandNormalIconData: toImageData(brand?.normalIconUrl)
  };
};

const channelGroupManager: ChannelGroupManager =
  new DefaultChannelGroupManager();
const agencyManager: AgencyManager = new DefaultAgencyManager();
const adRequestSourceManager: AdRequestSourceManager =
  new DefaultAdRequestSourceManager();

export const useChannelGroupEditFormModel = (
  channelGroups: ChannelGroupListDTO[],
  /**
   * ECOPD-5572：存檔成功後通知外層重抓渠道清單。
   *
   * 這一頁本來就有同樣的過期問題：存完導回列表，外層那份渠道清單還是進來時抓的，
   * 看到的是舊名稱。既然停用那條要接，這條順手一起接 ——
   * 兩個呼叫點用的是同一支 `reloadChannelGroups`。
   */
  onChannelGroupChanged?: () => void
): ChannelGroupFormModelData => {
  const params = useParams<{ id: string }>();
  const id = +params.id;
  const [tab, setTab] = useState(ChannelGroupTabs.BASIC);
  const { loading, callAPIs } = useCallAPI();
  const [redirectPathData, setRedirectPathData] = useState<{
    pathname: string;
  }>();
  const [initChannelGroup, setInitChannelGroup] =
    useState<ChannelGroupFormData>();
  const [agencyOptions, setAgencyOptions] = useState<SelectOptions[]>([]);
  const [tenmaxCategoryOptions, setTenmaxCategoryOptions] = useState<
    SelectOptions[]
  >([]);

  useEffect(() => {
    if (isNaN(id)) {
      setRedirectPathData({
        pathname: `/system/channel-groups/${id}/${ROUTE_PATH.ERROR404}`
      });
    }
    callAPIs(
      [() => channelGroupManager.getChannelGroup(id)],
      (channelGroup: ChannelGroupDTO) => {
        // ECOPD-5566 起停用（ARCHIVED）渠道**不再**導去 404：仍要能看、能編輯，
        // 只在 UI 上標記，所以這裡沒有狀態檢查。
        setInitChannelGroup({
          ...channelGroup,
          // ECOPD-5581: 後端存的是 URL 字串，FileInput 要的是 { file, url }。
          // 展開成表單欄位，送出時再由 wrapForServer 換回
          // property.epaperProps.epaperDefaultImageUrl 並把這個欄位 omit 掉。
          epaperDefaultImageData: channelGroup.property?.epaperProps
            ?.epaperDefaultImageUrl
            ? { url: channelGroup.property.epaperProps.epaperDefaultImageUrl }
            : {},
          ...toBrandFormData(channelGroup)
        });
        callAPIs(
          [
            () =>
              agencyManager.getAgenciesOptions(`channel.${channelGroup.id}`),
            () => adRequestSourceManager.getTenmaxCategories()
          ],
          (
            agencyOptions: SelectOptions[],
            tenmaxCategories: SelectOptions[]
          ) => {
            setAgencyOptions(agencyOptions);
            setTenmaxCategoryOptions(
              tenmaxCategories.map((category) => ({
                value: category.value,
                label: i18n.t(`tenmaxCategory.labels.${category.label}`)
              }))
            );
          }
        );
      }
    );
  }, [id, channelGroups, callAPIs]);

  const backToDetail = useCallback(() => {
    setRedirectPathData({
      pathname: `/system/channel-groups/${id}`
    });
  }, [id]);

  const submit = useCallback(
    (formData: ChannelGroupFormData) => {
      callAPIs(
        [() => channelGroupManager.editChannelGroup(id, formData)],
        () => {
          onChannelGroupChanged?.();
          backToDetail();
        }
      );
    },
    [id, backToDetail, callAPIs, onChannelGroupChanged]
  );

  // [RKB-2303] currently supporting only two currencies: TWD and USD
  const currencyOptions = useMemo(
    () => createSelectOptions([Currency.NTD, Currency.USD], 'common.currency.'),
    []
  );

  const breadcrumbsRoutes = useMemo(
    () => [
      {
        path: '/system/channel-groups',
        breadcrumb: i18n.t('channelGroups.title')
      },
      {
        path: '/system/channel-groups/:id',
        breadcrumb: DynamicBreadcrumb,
        props: {
          label: initChannelGroup ? initChannelGroup.name : '',
          matchParam: 'id'
        }
      },
      {
        path: '/system/channel-groups/:id/edit',
        breadcrumb: DynamicBreadcrumb,
        props: {
          prefix: i18n.t('common.labels.edit'),
          label: initChannelGroup ? initChannelGroup.name : '',
          matchParam: 'id'
        }
      }
    ],
    [initChannelGroup]
  );

  const onTabChange = useCallback((tab: string | null) => {
    if (!tab) {
      return;
    }
    setTab(tab as ChannelGroupTabs);
  }, []);

  return {
    loading,
    title: i18n.t('channelGroups.titles.edit'),
    isEditing: true,
    tab,
    redirectPathData,
    agencyOptions,
    currencyOptions,
    tenmaxCategoryOptions,
    channelGroupId: id,
    initChannelGroup,
    breadcrumbsRoutes,
    onTabChange,
    submit,
    cancel: backToDetail
  };
};
